import z from "zod";
import type { PartnerFlow } from "@remi/enums/partner-flow";
import type { QuotingSource } from "@remi/enums/quoting-source";
import type { SowChangeStatus } from "@remi/enums/sow-change/status";
import type { SowChangeType } from "@remi/enums/sow-change-type";

import type { PanelInvariantErrorType } from "../../../remi-error/errorTypes/quoting";
import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const scopeOfWorkErrorTypeValues = [
  "UNKNOWN",
  "BID_NOT_FOUND",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "QUOTE_NOT_FOUND",
  "ASSIGNED_CONTRACTOR_NOT_FOUND",
  "MARKET_NOT_FOUND",
  "NO_LINE_ITEMS_FOR_WORK_ORDER",
  "CONTRACTOR_PRICING_NOT_AVAILABLE",
  "WORK_ORDER_PDF_GENERATION_FAILED",
  "WORK_ORDER_PDF_SHINGLE_LINE_NOT_IN_CATALOGUE",
  "MULTIPLE_PENDING_MATERIALS_BILLS",
  "MARKET_NOT_FOUND_FOR_MATERIALS_BILL",
  "CONTRACTOR_PRICE_MISSING_FOR_LINE_ITEM",
  "QUOTE_CREATED_TASK_NOT_FOUND",
  "REQUEST_FOR_QUOTE_NOT_FOUND_FROM_QUOTE_VERSION",
  "PROJECT_NOT_FOUND",
  "ACTIVE_QUOTE_NOT_FOUND",
  "INSURANCE_ADJUSTMENT_REQUIRES_ESTIMATE_CONVERSION_QUOTE",
  "CUSTOM_LINE_ITEM_MISSING_PROJECT_CATEGORY",
  "TASK_NOT_FOUND",
  "PROJECT_ALREADY_COMPLETED",
  "WORK_ORDER_RESUBMIT_NOT_ALLOWED",
  "WORK_ORDER_LOCKED_AFTER_COST_VERIFICATION",
  "CHANGE_ORDER_FINANCIAL_EVENT_CAPTURE_FAILED",
  "CHANGE_ORDER_NOT_FOUND",
  "CHANGE_ORDER_LINE_ITEM_OWNERSHIP_MISMATCH",
  "CHANGE_ORDER_INVALID_STATUS_TRANSITION",
  "CHANGE_ORDER_NOT_ELIGIBLE",
  "CHANGE_ORDER_UNAUTHORIZED_MARGIN_OVERRIDE",
  "QUOTE_VERSION_NOT_FOUND",
  "HOMEOWNER_CONTACT_INCOMPLETE",
  "SOW_CHANGE_TYPE_NOT_ALLOWED_FOR_FLOW",
  "LINE_ITEM_TYPE_PAIR_NOT_IN_CATALOG",
  "QUOTE_VERSION_EXPIRED_AND_UNSIGNED",
  "SOW_CONFIRMATION_TARGET_MISMATCH",
  "FINALIZED_QUOTE_PANEL_EDIT_INVALID",
  "FINALIZE_QUOTE_NOTE_FAILED",
  "CONCESSIONS_DISABLED",
  "WORK_ORDER_ACCEPTANCE_IN_PROGRESS",
  "CONCESSION_ALREADY_OPEN",
  "CONCESSION_STATUS_CONFLICT",
  "WORK_ORDER_VERSION_STALE",
  "CONCESSION_CONTRACTOR_NOT_FOUND",
  "CONCESSION_ACTIVE_WORK_ORDER_NOT_FOUND",
  "WORK_ORDER_REJECTION_REVIEW_ALREADY_DECIDED",
  "WORK_ORDER_REJECTION_REVIEW_NOT_LIVE",
  "CONCESSION_TRANSITION_NOT_ALLOWED",
  "WORK_ORDER_CONCESSION_PRICE_EDIT_REFUSED",
  "WORK_ORDER_CONCESSION_NO_LONGER_APPROVED",
  "CONCESSION_CONTRACTOR_CHANGED",
  "CONCESSION_REQUEST_SYNCING",
  "CONCESSION_AMOUNT_EXCEEDS_CAP",
  "CONCESSION_AMOUNT_EXCEEDS_OPS_APPROVAL",
  "CONCESSION_WORK_ORDER_TOTAL_MISMATCH",
  "WORK_ORDER_SCOPE_REPORT_NOT_APPROVABLE",
  "CONCESSION_TYPE_ALREADY_APPLIED",
  "CONCESSION_REPRICE_DROP_SKIPPED",
  "SOW_CHANGE_FLOW_MISSING",
  "SOW_CHANGE_EXISTING_QUOTE_QUEUE_NOT_EMPTY",
  "SOW_CHANGE_INSURANCE_TYPE_QUEUE_NOT_EMPTY",
  "SOW_CHANGE_BLOCKED_PROJECTS_INVALID",
  "SOW_CHANGE_ALREADY_CLOSED",
  "SOW_CHANGE_NOT_FOUND",
  "SOW_CHANGE_REVIEW_REQUEST_INVALID",
  "SOW_CHANGE_REVIEW_SNAPSHOT_MISSING",
  "SOW_CHANGE_QUOTE_NOT_STAGED",
  "SOW_CHANGE_QUOTE_NOT_PROMOTED",
  "SOW_CHANGE_ADDENDUM_DECISION_INVALID",
  "SOW_CHANGE_ADDENDUM_NOT_SIGNED",
  "SOW_CHANGE_VERIFY_HELD",
  "SOW_CHANGE_NOT_FUNDED",
  "SOW_CHANGE_ADDENDUM_EXCEEDS_APPROVED_CREDIT",
  "SOW_CHANGE_ADDENDUM_ALREADY_SENT",
  "SOW_CHANGE_BLOCKED_PROJECTS_UPDATE_UNAUTHORIZED",
  "SOW_CHANGE_BLOCKER_NOT_DELETABLE",
] as const;
const scopeOfWorkErrorTypeSchema = z.enum(scopeOfWorkErrorTypeValues);
export const ScopeOfWorkErrorType = scopeOfWorkErrorTypeSchema.enum;
type ScopeOfWorkErrorType = z.infer<typeof scopeOfWorkErrorTypeSchema>;

export type ScopeOfWorkErrors =
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_ACCEPTANCE_IN_PROGRESS;
      data: { taskId: string; workOrderId: number };
    }
  | { errorType: typeof ScopeOfWorkErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof ScopeOfWorkErrorType.CONCESSIONS_DISABLED; data: { taskId: string; reasonKey: string } }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_ALREADY_OPEN;
      data: { taskId: string; reasonKey: string; openReviewTaskId: string | null; openConcessionId: string | null };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_STATUS_CONFLICT;
      data: { concessionId: string; expectedStatus: string; actualStatus: string | null; targetStatus: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_VERSION_STALE;
      data: {
        projectId: number;
        taskId: string;
        submittedWorkOrderId: number | null;
        activeWorkOrderId: number | null;
      };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_CONTRACTOR_NOT_FOUND;
      data: { projectId: number; taskEventId: string | null };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_ACTIVE_WORK_ORDER_NOT_FOUND;
      data: { projectId: number; taskEventId: string | null };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_TRANSITION_NOT_ALLOWED;
      data: { concessionId: string; fromStatus: string; toStatus: string; refusal: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_REJECTION_REVIEW_ALREADY_DECIDED;
      data: { taskId: string; status: string; claimed: boolean };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_REJECTION_REVIEW_NOT_LIVE;
      data: { taskId: string; projectId: number; refusal: "missing" | "completed" | "no-host" | "project-mismatch" };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_CONCESSION_PRICE_EDIT_REFUSED;
      data: { taskId: string; projectId: number; refusal: string; workOrderLineItemId: string | null };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_CONCESSION_NO_LONGER_APPROVED;
      data: { projectId: number; concessionIds: string[] };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_CONTRACTOR_CHANGED;
      data: {
        projectId: number;
        expectedContractorOrganizationId: string | null;
        actualContractorOrganizationId: string | null;
      };
    }
  | { errorType: typeof ScopeOfWorkErrorType.CONCESSION_REQUEST_SYNCING; data: { taskId: string; projectId: number } }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_AMOUNT_EXCEEDS_CAP;
      data: { taskId: string; concessionId: string; approvedAmountCents: number; capCents: number };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_AMOUNT_EXCEEDS_OPS_APPROVAL;
      data: {
        taskId: string;
        concessionId: string;
        approvedAmountCents: number;
        opsApprovedAmountCents: number | null;
      };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_WORK_ORDER_TOTAL_MISMATCH;
      data: {
        projectId: number;
        concessionId: string;
        priorAmountCents: number;
        nextAmountCents: number;
        approvedAmountCents: number;
      };
    }
  | { errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_SCOPE_REPORT_NOT_APPROVABLE; data: { taskId: string } }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_REPRICE_DROP_SKIPPED;
      data: { projectId: number; concessionIds: string[]; contractorId: string; cause: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONCESSION_TYPE_ALREADY_APPLIED;
      data: { taskId: string; reasonKey: string; concessionType: string; workOrderId: number };
    }
  | { errorType: typeof ScopeOfWorkErrorType.BID_NOT_FOUND; data: { bidId: number } }
  | { errorType: typeof ScopeOfWorkErrorType.REQUEST_FOR_QUOTE_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof ScopeOfWorkErrorType.QUOTE_NOT_FOUND; data: { requestForQuoteId?: number; quoteId?: string } }
  | { errorType: typeof ScopeOfWorkErrorType.ASSIGNED_CONTRACTOR_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof ScopeOfWorkErrorType.MARKET_NOT_FOUND;
      data: { projectId: number; propertyAddressId: string; coordinates?: { longitude: number; latitude: number } };
    }
  | { errorType: typeof ScopeOfWorkErrorType.NO_LINE_ITEMS_FOR_WORK_ORDER; data: { projectId: number; reason: string } }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONTRACTOR_PRICING_NOT_AVAILABLE;
      data: { projectId: number; contractorId: string; marketId: number };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_PDF_GENERATION_FAILED;
      data: { projectId: number; workOrderId?: number; taskId?: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_PDF_SHINGLE_LINE_NOT_IN_CATALOGUE;
      data: { projectId: number; workOrderId?: number; shingleLine?: string; shingleColor: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.MULTIPLE_PENDING_MATERIALS_BILLS;
      data: { projectId: number; billIds: string[]; billType?: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.MARKET_NOT_FOUND_FOR_MATERIALS_BILL;
      data: { projectId: number; propertyAddressId: string; coordinates?: { longitude: number; latitude: number } };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CONTRACTOR_PRICE_MISSING_FOR_LINE_ITEM;
      data: { projectId: number; lineItemName: string; pricingType: string; marketName?: string; contractorId: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.QUOTE_CREATED_TASK_NOT_FOUND;
      data: { bidId: number };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.REQUEST_FOR_QUOTE_NOT_FOUND_FROM_QUOTE_VERSION;
      data: { quoteVersionId: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.PROJECT_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.ACTIVE_QUOTE_NOT_FOUND;
      data: { requestForQuoteId?: number };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.INSURANCE_ADJUSTMENT_REQUIRES_ESTIMATE_CONVERSION_QUOTE;
      data: { requestForQuoteId: number; quotingSource: QuotingSource };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CUSTOM_LINE_ITEM_MISSING_PROJECT_CATEGORY;
      data: { lineItemTypeName: string; customName?: string | null };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.LINE_ITEM_TYPE_PAIR_NOT_IN_CATALOG;
      data: { bidId: number; pairs: string[] };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.TASK_NOT_FOUND;
      data: { bidId: number };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.PROJECT_ALREADY_COMPLETED;
      data: { bidId: number };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_RESUBMIT_NOT_ALLOWED;
      data: { projectId: number; taskId: string; reason: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.WORK_ORDER_LOCKED_AFTER_COST_VERIFICATION;
      data: {
        projectId: number;
        taskId: string;
        writeSource: string;
        verifyContractorCostsCompletedAt: string | null;
      };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CHANGE_ORDER_FINANCIAL_EVENT_CAPTURE_FAILED;
      data: { changeOrderId: string; projectId: number; eventType: string; cause: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CHANGE_ORDER_NOT_FOUND;
      data: { changeOrderId: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CHANGE_ORDER_LINE_ITEM_OWNERSHIP_MISMATCH;
      data: { changeOrderId: string; invalidLineItemIds: string[] };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CHANGE_ORDER_INVALID_STATUS_TRANSITION;
      data: { changeOrderId: string; currentStatus: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CHANGE_ORDER_NOT_ELIGIBLE;
      data: { projectId: number; reason: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.CHANGE_ORDER_UNAUTHORIZED_MARGIN_OVERRIDE;
      data: { changeOrderId: string };
    }
  | { errorType: typeof ScopeOfWorkErrorType.QUOTE_VERSION_NOT_FOUND; data: { quoteVersionId: string } }
  | {
      errorType: typeof ScopeOfWorkErrorType.HOMEOWNER_CONTACT_INCOMPLETE;
      data: { bidId: number; hasEmail: boolean; hasPhone: boolean };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_TYPE_NOT_ALLOWED_FOR_FLOW;
      data: { bidId: number; partnerFlow: PartnerFlow; phase: "preNtp" | "postNtp"; type: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.QUOTE_VERSION_EXPIRED_AND_UNSIGNED;
      data: { bidId: number; quoteVersionId: string; expiresAt: string | null };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CONFIRMATION_TARGET_MISMATCH;
      data: { bidId: number; homeownerId: string; propertyAddressId: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.FINALIZED_QUOTE_PANEL_EDIT_INVALID;
      data: { quoteVersionId: string; requestForQuoteId: number; invariant: PanelInvariantErrorType };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.FINALIZE_QUOTE_NOTE_FAILED;
      data: { taskId?: string; quoteVersionId?: string; bidId: number };
    }
  | { errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_FLOW_MISSING; data: { bidId: number; workflowVersion: number } }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_EXISTING_QUOTE_QUEUE_NOT_EMPTY;
      data: { bidId: number; openSowChangeIds: string[] };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_INSURANCE_TYPE_QUEUE_NOT_EMPTY;
      data: { bidId: number; type: SowChangeType; openSowChangeIds: string[] };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_BLOCKED_PROJECTS_INVALID;
      data: {
        bidId: number;
        invalidReason: "EMPTY" | "NOT_ON_BID" | "QA_PROJECT" | "NOT_ACTIVE";
        invalidProjectIds: number[];
      };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_ALREADY_CLOSED;
      data: { sowChangeId: string; status: SowChangeStatus };
    }
  | { errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_NOT_FOUND; data: { sowChangeId: string } }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_REVIEW_REQUEST_INVALID;
      data: { sowChangeId: string; invalidReason: "TYPE_CHANGED" | "BID_CHANGED" | "REQUEST_FOR_QUOTE_CHANGED" };
    }
  | { errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_REVIEW_SNAPSHOT_MISSING; data: { sowChangeId: string } }
  | { errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_QUOTE_NOT_STAGED; data: { sowChangeId: string } }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_QUOTE_NOT_PROMOTED;
      data: { sowChangeId: string; quoteVersionId: string | null; activeQuoteVersionId: string | null };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_ADDENDUM_DECISION_INVALID;
      data: { sowChangeId: string; taskId: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_ADDENDUM_NOT_SIGNED;
      data: { sowChangeId: string; taskId: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_VERIFY_HELD;
      data: { sowChangeId: string; taskId: string; openHoldupCount: number };
    }
  | { errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_NOT_FUNDED; data: { sowChangeId: string; taskId: string } }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_ADDENDUM_EXCEEDS_APPROVED_CREDIT;
      data: { requestForQuoteId: number; loanAmount: number; maxQualifiedAmount: number };
    }
  | { errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_ADDENDUM_ALREADY_SENT; data: { taskId: string } }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_BLOCKED_PROJECTS_UPDATE_UNAUTHORIZED;
      data: { sowChangeId: string; taskId: string };
    }
  | {
      errorType: typeof ScopeOfWorkErrorType.SOW_CHANGE_BLOCKER_NOT_DELETABLE;
      data: { sowChangeId: string; blockerId: string };
    };

export const scopeOfWorkErrorDetails: Record<ScopeOfWorkErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  BID_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bid not found" },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote not found for work order",
  },
  ASSIGNED_CONTRACTOR_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Assigned contractor not found",
  },
  MARKET_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Market not found" },
  NO_LINE_ITEMS_FOR_WORK_ORDER: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "No line items available for work order",
  },
  CONTRACTOR_PRICING_NOT_AVAILABLE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor pricing not available",
  },
  WORK_ORDER_PDF_GENERATION_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Work order PDF generation failed",
  },
  WORK_ORDER_PDF_SHINGLE_LINE_NOT_IN_CATALOGUE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Work order PDF shingle line was not a catalogue value, so the swatch was omitted",
  },
  MULTIPLE_PENDING_MATERIALS_BILLS: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Multiple pending materials bills found",
  },
  MARKET_NOT_FOUND_FOR_MATERIALS_BILL: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Market not found for materials bill calculation",
  },
  CONTRACTOR_PRICE_MISSING_FOR_LINE_ITEM: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor price missing for line item",
  },
  QUOTE_CREATED_TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote created task not found after quote creation",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND_FROM_QUOTE_VERSION: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found from quote version",
  },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project not found",
  },
  ACTIVE_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Active quote not found",
  },
  INSURANCE_ADJUSTMENT_REQUIRES_ESTIMATE_CONVERSION_QUOTE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Insurance adjustment attempted on non-estimate-conversion active quote",
  },
  CUSTOM_LINE_ITEM_MISSING_PROJECT_CATEGORY: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Custom line item is missing required project category",
  },
  LINE_ITEM_TYPE_PAIR_NOT_IN_CATALOG: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Line item (name, pricingType) pair does not exist in the LineItemType catalog",
  },
  TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task not found for bid",
  },
  PROJECT_ALREADY_COMPLETED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "This project has already been completed. No further action is needed.",
  },
  WORK_ORDER_RESUBMIT_NOT_ALLOWED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Work order resubmit is only allowed on CORRECTIONS projects before RACWO completes.",
  },
  WORK_ORDER_LOCKED_AFTER_COST_VERIFICATION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "The work order is locked because contractor costs were already verified and billed.",
  },
  CHANGE_ORDER_FINANCIAL_EVENT_CAPTURE_FAILED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "Change order financial event capture failed",
  },
  CHANGE_ORDER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Change order not found",
  },
  CHANGE_ORDER_LINE_ITEM_OWNERSHIP_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Change order line item IDs do not belong to the specified change order",
  },
  CHANGE_ORDER_INVALID_STATUS_TRANSITION: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Change order is not in a reviewable status",
  },
  CHANGE_ORDER_NOT_ELIGIBLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Change order submission not allowed at current project stage",
  },
  CHANGE_ORDER_UNAUTHORIZED_MARGIN_OVERRIDE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Caller lacks changeOrder.adjustMargin capability",
  },
  QUOTE_VERSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote version not found for SOW change project assignment",
  },
  HOMEOWNER_CONTACT_INCOMPLETE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Homeowner is missing email or phone for SOW confirmation",
  },
  SOW_CHANGE_TYPE_NOT_ALLOWED_FOR_FLOW: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "SOW change type not allowed for this partner flow and phase",
  },
  QUOTE_VERSION_EXPIRED_AND_UNSIGNED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Selected quote version is expired and has no executed agreement",
  },
  SOW_CONFIRMATION_TARGET_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "SOW confirmation named a homeowner or address that is not the bid's worksite",
  },
  FINALIZED_QUOTE_PANEL_EDIT_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quote finalize blocked: hand-edited solar panel line items failed a panel invariant",
  },
  FINALIZE_QUOTE_NOTE_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "A finalized quote's task note was not posted",
  },
  WORK_ORDER_ACCEPTANCE_IN_PROGRESS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "A new concession would block recovery of work order acceptance",
  },
  CONCESSIONS_DISABLED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Work order concession requests are turned off",
  },
  CONCESSION_ALREADY_OPEN: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "A work order concession request is already under review for this work order",
  },
  CONCESSION_STATUS_CONFLICT: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "The work order concession changed status before this decision was saved",
  },
  WORK_ORDER_VERSION_STALE: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "The submitted work order is no longer the project's active work order",
  },
  CONCESSION_CONTRACTOR_NOT_FOUND: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A work order concession was requested on a project with no assigned contractor",
  },
  CONCESSION_ACTIVE_WORK_ORDER_NOT_FOUND: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A work order concession was requested on a project with no active work order",
  },
  CONCESSION_TRANSITION_NOT_ALLOWED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A work order concession status change is not an allowed transition",
  },
  WORK_ORDER_REJECTION_REVIEW_ALREADY_DECIDED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "This work order rejection review is already being decided or is closed",
  },
  WORK_ORDER_REJECTION_REVIEW_NOT_LIVE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A work order rejection review is closed or belongs to another project",
  },
  WORK_ORDER_CONCESSION_PRICE_EDIT_REFUSED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A work order write tried to change a line whose price must not change",
  },
  WORK_ORDER_CONCESSION_NO_LONGER_APPROVED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "A Create Work Order included a concession that was no longer Finance-approved",
  },
  CONCESSION_CONTRACTOR_CHANGED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "The project's contractor changed before a concession work order was written",
  },
  CONCESSION_REQUEST_SYNCING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A work order concession request was decided before its details finished saving",
  },
  CONCESSION_AMOUNT_EXCEEDS_CAP: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A work order concession amount is above the concession cap",
  },
  CONCESSION_AMOUNT_EXCEEDS_OPS_APPROVAL: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Finance approved a work order concession above the amount Ops approved",
  },
  CONCESSION_WORK_ORDER_TOTAL_MISMATCH: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "The regenerated work order total does not equal the prior total plus the approved concession",
  },
  WORK_ORDER_SCOPE_REPORT_NOT_APPROVABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A scope report was approved as a work order concession",
  },
  CONCESSION_REPRICE_DROP_SKIPPED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A reassignment reprice dropped an applied concession line but did not withdraw the concession",
  },
  CONCESSION_TYPE_ALREADY_APPLIED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "A work order concession was requested for a type the work order already carries",
  },
  SOW_CHANGE_FLOW_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "The workflow version has no SOW change flow, so the SOW change could not be filed",
  },
  SOW_CHANGE_EXISTING_QUOTE_QUEUE_NOT_EMPTY: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "An existing-quote SOW change can't be filed while another SOW change is open on the bid",
  },
  SOW_CHANGE_INSURANCE_TYPE_QUEUE_NOT_EMPTY: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription:
      "An insurance supplement or elective SOW change can't be filed while a SOW change is open on the bid",
  },
  SOW_CHANGE_BLOCKED_PROJECTS_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "The selected projects must be one or more active, non-QA projects on this bid",
  },
  SOW_CHANGE_ALREADY_CLOSED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "This SOW change is already completed or abandoned",
  },
  SOW_CHANGE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "SOW change not found",
  },
  SOW_CHANGE_REVIEW_REQUEST_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "SOW change review edited the request into a different change",
  },
  SOW_CHANGE_REVIEW_SNAPSHOT_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "SOW change quote was built before its review was snapshotted",
  },
  SOW_CHANGE_QUOTE_NOT_STAGED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "SOW change has no staged quote for this step",
  },
  SOW_CHANGE_QUOTE_NOT_PROMOTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "SOW change verification stopped because its staged quote is not the active quote",
  },
  SOW_CHANGE_ADDENDUM_DECISION_INVALID: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "The task is not this SOW change's open declined-addendum decision",
  },
  SOW_CHANGE_ADDENDUM_NOT_SIGNED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "SOW change was verified before its change order addendum was signed",
  },
  SOW_CHANGE_VERIFY_HELD: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "SOW change was verified while a holdup still held its VERIFY task",
  },
  SOW_CHANGE_NOT_FUNDED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "SOW change was verified before the bid was funded for its staged quote",
  },
  SOW_CHANGE_ADDENDUM_EXCEEDS_APPROVED_CREDIT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "SOW change addendum would move the loan above the homeowner's approved credit",
  },
  SOW_CHANGE_ADDENDUM_ALREADY_SENT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "SOW change addendum was sent again without a resend",
  },
  SOW_CHANGE_BLOCKED_PROJECTS_UPDATE_UNAUTHORIZED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Caller lacks sowChange.updateBlockedProjects for a SOW change review's project edit",
  },
  SOW_CHANGE_BLOCKER_NOT_DELETABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "A SOW change's project blocker was deleted instead of released through its change",
  },
};
